import React, { ChangeEvent, useRef, useState } from 'react';
import { getMessage } from '@tcc/i18n';
import { Icon } from '@tcc/crate';

import { isFile, validateFiles } from '../../../../../utils/scoutingUtils';
import { ScoutingFormIsEditingTitle, ScoutingFormListItem } from '..';
import { FilesViewer } from '../FilesViewer/FilesViewer';
import { FilesInput } from '../FilesInput/FilesInput';

import formStyles from '../scoutingForm.css';
import ConfirmationModal from '../../../../Modal/ConfirmationModal';
import { ModalWrapper } from '../../../../../components/ModalWrapper/ModalWrapper';

export enum FilesEditAdditionType {
  ADD = 'ADD',
  REPLACE = 'REPLACE',
}

export type FilesEditViewProps = {
  previews: string[];
  indexToEdit?: number;
  onClose: () => void;
  onDelete: (index: number) => void;
  onReplace: (index: number, newFile: File) => void;
  onAdd: (newFile: File[]) => void;
  confirmationModal: boolean;
  setConfirmationModal: (showConfirmationModal: boolean) => void;
};

export const FilesEditView = ({ previews, indexToEdit = 0, onClose, onDelete, onAdd, onReplace, confirmationModal, setConfirmationModal }: FilesEditViewProps) => {
  const [index, setIndex] = useState<number>(indexToEdit);
  const inputFile = useRef<HTMLInputElement | null>(null);
  const additionType = useRef<FilesEditAdditionType>(FilesEditAdditionType.ADD);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [warningModalMessage, setWarningModalMessage] = useState('');
  const selectedFiles = useRef<File[]>([]); // To keep track of selected files

  const photosAmountLabel = getMessage('enhancedScouting.form.viewer.currentPhoto', {
    currentPhotoNumber: index + 1,
    totalAmountOfPhotos: previews.length,
  });

  const onDeleteFile = () => {
    if (isFile(previews[index])) {
      try {
        URL.revokeObjectURL(previews[index]);
      } catch {
        // If clearing the memory fails, there is nothing else to do.
      }
    }
    onDelete(index);
    onClose();
    setConfirmationModal(false)
  };

  const onClickAssetDelete = () => {
    setConfirmationModal(true);
  }


  const onFilesSelected = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;

    const files = Array.from(e.target.files);

    // Validate the selected files
    const { validFiles, invalidFileMessage } = validateFiles(
      files,
      selectedFiles.current.length, // Update this with your actual selected files count
      previews.length,             // Update this with your actual previews count
    );

    if (invalidFileMessage) {
      setWarningModalMessage(invalidFileMessage);
      setShowWarningModal(true);
      return;
    }

    // Proceed based on the addition type
    if (additionType.current === FilesEditAdditionType.ADD) {
      onAdd(validFiles);
    } else if (files[0]) {
      onReplace(index, validFiles[0]); // Use the first valid file
    }
  };
  const onFilesInputMounted = (ref: React.MutableRefObject<HTMLInputElement | null>) => {
    inputFile.current = ref.current
  };

  const onReplaceSelected = () => {
    if (!inputFile.current) return;

    additionType.current = FilesEditAdditionType.REPLACE;
    inputFile.current.multiple = false;
    inputFile.current.click();
  };

  const onAddSelected = () => {
    if (!inputFile.current) return;

    additionType.current = FilesEditAdditionType.ADD;
    inputFile.current.multiple = true;
    inputFile.current.click();
  };

  return <>
    <ScoutingFormIsEditingTitle
      title={photosAmountLabel}
      leftLabel=""
      rightLabel=""
      onLeft={onClose}
      onRight={onClickAssetDelete}
      customRightIcon={Icon.set.TRASH}
    />
    {showWarningModal && (
      <ModalWrapper>
        <ConfirmationModal
          isWarningModal
          className={formStyles.warningModal}
          handleClickConfirm={() => setShowWarningModal(false)}
          handleClickGoBack={() => setShowWarningModal(false)}
          handleClickCancel={() => setShowWarningModal(false)}
          title={warningModalMessage}
        />
      </ModalWrapper>
    )}
    <FilesInput onMount={onFilesInputMounted} onChange={onFilesSelected} />
    <div className={formStyles.modalContent}>
      <FilesViewer urls={previews} onNavigation={setIndex} initialIndex={index} />
      <ScoutingFormListItem showArrow={false} onClick={onReplaceSelected}>
        {getMessage('enhancedScouting.form.viewer.replacePhoto')}
      </ScoutingFormListItem>
      <ScoutingFormListItem showArrow={false} onClick={onAddSelected}>
        {getMessage('enhancedScouting.form.viewer.addPhoto')}
      </ScoutingFormListItem>
      {confirmationModal && (
        <ModalWrapper>
          <ConfirmationModal
            handleClickConfirm={() => onDeleteFile()}
            handleClickGoBack={() => setConfirmationModal(false)}
            handleClickCancel={() => setConfirmationModal(false)}
            confirmButtonText={getMessage('regions.report.modal.delete')}
            title={getMessage('enhancedScouting.form.confirmationModal.deleteImage')}
            confirmButtonMode="negative"
          />
        </ModalWrapper>
      )}
    </div>
  </>
};
